import React, { useEffect, useState, useRef } from "react";
import { Plus, Edit2, Trash2, X, Save, Upload, Image as ImageIcon } from "lucide-react";
import {
  fetchProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminUploadImage,
  resolveImage,
} from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  category: "Impressoras",
  subcategory: "",
  description: "",
  highlights: "",
  image: "",
  action: "Solicitar Orçamento",
  type: "venda",
};

const CATEGORIES = ["Impressoras", "Ribbons", "Etiquetas"];
const TYPES = ["venda", "locacao", "ambos"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Imagem enviada!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha no upload";
      toast.error(typeof msg === "string" ? msg : "Falha no upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const reload = () => {
    setLoading(true);
    fetchProducts()
      .then(setProducts)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      category: p.category || "Impressoras",
      subcategory: p.subcategory || "",
      description: p.description || "",
      highlights: (p.highlights || []).join("\n"),
      image: p.image || "",
      action: p.action || "Solicitar Orçamento",
      type: p.type || "venda",
    });
    setEditing(p.id);
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("Selecione ou envie uma imagem do produto.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      subcategory: form.subcategory || null,
      highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        await adminCreateProduct(payload);
        toast.success("Produto criado");
      } else {
        await adminUpdateProduct(editing, payload);
        toast.success("Produto atualizado");
      }
      close();
      reload();
    } catch (err) {
      toast.error("Erro ao salvar produto");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast.success("Produto excluído");
      reload();
    } catch {
      toast.error("Falha ao excluir");
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-products-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-3">// CATÁLOGO</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Produtos</h1>
          <p className="mt-2 text-sm text-[#6B7280]">Crie, edite e remova produtos exibidos no site.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="admin-product-new"
          className="inline-flex items-center gap-2 bg-[#E84E34] text-white font-bold px-5 py-3 uppercase text-xs tracking-[0.18em] hover:bg-[#CF412A] transition-colors"
        >
          <Plus size={16} /> Novo produto
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B7280]">Carregando...</div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left font-mono-sm text-[10px] tracking-[0.2em] uppercase text-[#6B7280] px-5 py-4">Produto</th>
                <th className="text-left font-mono-sm text-[10px] tracking-[0.2em] uppercase text-[#6B7280] px-5 py-4">Categoria</th>
                <th className="text-left font-mono-sm text-[10px] tracking-[0.2em] uppercase text-[#6B7280] px-5 py-4">Tipo</th>
                <th className="text-right font-mono-sm text-[10px] tracking-[0.2em] uppercase text-[#6B7280] px-5 py-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-[#E5E7EB] hover:bg-[#F9F9F9]" data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img src={resolveImage(p.image)} alt="" className="w-12 h-12 object-cover border border-[#E5E7EB] bg-[#F9F9F9]" />
                      <div>
                        <div className="font-bold text-[#1A1A1A]">{p.name}</div>
                        <div className="text-xs text-[#6B7280] line-clamp-1 max-w-md">{p.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-xs font-mono-sm uppercase tracking-[0.15em] text-[#E84E34]">{p.category}</span>
                    {p.subcategory && <span className="text-xs text-[#6B7280]"> / {p.subcategory}</span>}
                  </td>
                  <td className="px-5 py-4 text-xs uppercase tracking-wide">{p.type}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`admin-product-edit-${p.id}`}
                        className="p-2 border border-[#E5E7EB] hover:border-[#1A1A1A]"
                        aria-label="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(p)}
                        data-testid={`admin-product-delete-${p.id}`}
                        className="p-2 border border-[#E5E7EB] hover:bg-[#E84E34] hover:text-white hover:border-[#E84E34]"
                        aria-label="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-[#6B7280]">Nenhum produto cadastrado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 md:p-10" data-testid="admin-product-modal">
          <form onSubmit={onSave} className="bg-white w-full max-w-2xl border-t-4 border-[#E84E34]">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
              <h2 className="font-display text-2xl font-black">
                {editing === "new" ? "Novo produto" : "Editar produto"}
              </h2>
              <button type="button" onClick={close} className="p-2 hover:bg-[#F9F9F9]" data-testid="admin-product-modal-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <Field label="Nome *" testid="admin-product-name">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
                />
              </Field>

              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Categoria *">
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none bg-white"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Subcategoria">
                  <input
                    value={form.subcategory}
                    onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
                    placeholder="Ex: Térmica"
                  />
                </Field>
                <Field label="Tipo *">
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none bg-white"
                  >
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Descrição *">
                <textarea
                  required
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none resize-none"
                />
              </Field>

              <Field label="Destaques (um por linha)">
                <textarea
                  value={form.highlights}
                  onChange={(e) => setForm({ ...form, highlights: e.target.value })}
                  rows={4}
                  className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none resize-none font-mono-sm text-xs"
                  placeholder={"Destaque 1\nDestaque 2"}
                />
              </Field>

              <Field label="Imagem do produto *">
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-product-upload-btn"
                      className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:bg-[#E84E34] transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      <Upload size={14} />
                      {uploading ? "Enviando..." : "Enviar imagem"}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      data-testid="admin-product-file"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                    <input
                      type="text"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      placeholder="ou cole uma URL externa..."
                      className="flex-1 border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
                    />
                  </div>
                  {form.image ? (
                    <div className="flex items-start gap-3">
                      <img
                        src={resolveImage(form.image)}
                        alt="preview"
                        className="w-32 h-24 object-cover border border-[#E5E7EB] bg-[#F9F9F9]"
                        onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image: "" })}
                        className="text-xs text-[#E84E34] underline hover:no-underline"
                      >
                        Remover
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                      <ImageIcon size={14} /> Nenhuma imagem selecionada (PNG, JPG, WEBP — máx 5MB)
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Texto do botão CTA">
                <input
                  value={form.action}
                  onChange={(e) => setForm({ ...form, action: e.target.value })}
                  className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
                  placeholder="Ex: Locar via WhatsApp"
                />
              </Field>
            </div>

            <div className="p-6 border-t border-[#E5E7EB] flex justify-end gap-3">
              <button
                type="button"
                onClick={close}
                className="px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] border border-[#E5E7EB] hover:border-[#1A1A1A]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-product-save"
                className="inline-flex items-center gap-2 bg-[#E84E34] text-white font-bold px-6 py-3 uppercase text-xs tracking-[0.18em] hover:bg-[#CF412A] disabled:opacity-60"
              >
                {saving ? "Salvando..." : "Salvar"} <Save size={14} />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-2">{label}</label>
    {children}
  </div>
);
